/**
 * Agenda do DP — Alertas Preditivos
 * /dp-folha/alertas
 *
 * Lista completa de alertas com filtros (severidade, categoria) e navegação direta.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Bell, Building2, Loader2, RefreshCw, ArrowRight, CalendarClock, CalendarHeart,
  Cake, AlertTriangle, FileCode,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const ALERTA_SEV_BADGE = {
  alta: 'bg-red-100 text-red-700 border-red-300',
  media: 'bg-amber-100 text-amber-700 border-amber-300',
  baixa: 'bg-blue-100 text-blue-700 border-blue-300',
};

const CATEGORIA_ICON = {
  ferias_em_atraso: CalendarClock,
  ferias_proximas: CalendarHeart,
  experiencia_termina: CalendarClock,
  aniversario: Cake,
  aso_vencido: AlertTriangle,
  aso_proximo: Bell,
  prazo_esocial: FileCode,
  prazo_reinf: FileCode,
};

const CATEGORIA_LABEL = {
  ferias_em_atraso: 'Férias Vencidas',
  ferias_proximas: 'Férias Próximas',
  experiencia_termina: 'Fim Experiência',
  aniversario: 'Aniversários',
  aso_vencido: 'ASO Vencido',
  aso_proximo: 'ASO Próximo',
  prazo_esocial: 'Prazo eSocial',
  prazo_reinf: 'Prazo Reinf',
};

export default function AlertasPage() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [alertas, setAlertas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtroSev, setFiltroSev] = useState('todas');
  const [filtroCat, setFiltroCat] = useState('todas');
  const [janela, setJanela] = useState('60');

  useEffect(() => {
    axios.get(`${API}/api/companies`, auth())
      .then(r => setCompanies(r.data || []))
      .catch(() => { /* sem acesso à listagem de empresas */ });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { janela_dias: parseInt(janela), limit: 500 };
      if (companyId) params.company_id = companyId;
      if (filtroSev !== 'todas') params.severidade = filtroSev;
      if (filtroCat !== 'todas') params.categoria = filtroCat;
      const r = await axios.get(`${API}/api/dp-folha/alertas`, { ...auth(), params });
      setAlertas(r.data || []);
    } finally { setLoading(false); }
  }, [companyId, filtroSev, filtroCat, janela]);
  useEffect(() => { load(); }, [load]);

  const totais = alertas.reduce((acc, a) => {
    acc[a.severity] = (acc[a.severity] || 0) + 1; return acc;
  }, {});

  return (
    <div className="p-6 space-y-4 max-w-7xl mx-auto" data-testid="alertas-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Bell className="h-7 w-7 text-indigo-600" />Agenda do DP <Badge variant="outline" className="ml-2">Alertas Preditivos</Badge>
          </h1>
          <p className="text-sm text-slate-500">Tudo que requer ação nos próximos dias — férias, ASO, experiência, prazos legais.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={companyId || 'todas'} onValueChange={v => setCompanyId(v === 'todas' ? '' : v)}>
            <SelectTrigger className="w-56" data-testid="alertas-company-select"><Building2 className="h-4 w-4 mr-2" /><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todas">Todas as empresas</SelectItem>
              {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name || c.razao_social}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} data-testid="alertas-refresh-btn">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="border rounded-lg p-3 bg-slate-50"><div className="text-xs">Total</div><div className="text-2xl font-bold">{alertas.length}</div></div>
        <div className="border rounded-lg p-3 bg-red-50"><div className="text-xs">Alta prioridade</div><div className="text-2xl font-bold text-red-700">{totais.alta || 0}</div></div>
        <div className="border rounded-lg p-3 bg-amber-50"><div className="text-xs">Média prioridade</div><div className="text-2xl font-bold text-amber-700">{totais.media || 0}</div></div>
        <div className="border rounded-lg p-3 bg-blue-50"><div className="text-xs">Baixa prioridade</div><div className="text-2xl font-bold text-blue-700">{totais.baixa || 0}</div></div>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-base">Filtros</CardTitle>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Select value={filtroSev} onValueChange={setFiltroSev}>
              <SelectTrigger className="w-36" data-testid="alertas-filtro-severidade"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas severidades</SelectItem>
                <SelectItem value="alta">Alta</SelectItem>
                <SelectItem value="media">Média</SelectItem>
                <SelectItem value="baixa">Baixa</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filtroCat} onValueChange={setFiltroCat}>
              <SelectTrigger className="w-44" data-testid="alertas-filtro-categoria"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas categorias</SelectItem>
                {Object.entries(CATEGORIA_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={janela} onValueChange={setJanela}>
              <SelectTrigger className="w-36" data-testid="alertas-filtro-janela"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">7 dias</SelectItem>
                <SelectItem value="30">30 dias</SelectItem>
                <SelectItem value="60">60 dias</SelectItem>
                <SelectItem value="90">90 dias</SelectItem>
                <SelectItem value="365">1 ano</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Alertas</CardTitle>
          <CardDescription>Clique em qualquer alerta para ir ao módulo correto.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : (
            <div className="space-y-2">
              {alertas.length === 0 && <div className="text-center text-slate-400 py-8">Nenhum alerta para os filtros selecionados ✨</div>}
              {alertas.map((a, i) => {
                const Icon = CATEGORIA_ICON[a.category] || Bell;
                return (
                  <button key={i}
                          onClick={() => navigate(a.link || '/dp-folha/funcionarios')}
                          data-testid={`alerta-row-${i}`}
                          className="w-full text-left border rounded-lg p-3 hover:bg-slate-50 bg-white flex items-start gap-3">
                    <Icon className={`h-5 w-5 mt-0.5 shrink-0 ${a.severity === 'alta' ? 'text-red-600' : a.severity === 'media' ? 'text-amber-600' : 'text-blue-600'}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-medium text-sm">{a.title}</div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">{CATEGORIA_LABEL[a.category] || a.category}</Badge>
                          <Badge variant="outline" className={`${ALERTA_SEV_BADGE[a.severity]} text-xs`}>{a.severity}</Badge>
                        </div>
                      </div>
                      <div className="text-sm text-slate-600 mt-0.5">{a.description}</div>
                      {a.due_date && (
                        <div className="text-xs text-slate-400 mt-1">Prazo: {a.due_date}{a.days_remaining !== null && ` · ${a.days_remaining < 0 ? Math.abs(a.days_remaining) + ' dia(s) em atraso' : a.days_remaining + ' dia(s) restantes'}`}</div>
                      )}
                    </div>
                    <ArrowRight className="h-4 w-4 text-slate-400 self-center" />
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
